import { addToast } from '@heroui/react';
import { useRouter } from 'next/router';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import type { ToolUnlockModalEntry } from '../components/shared/ToolUnlockValueModal';
import { ToolUnlockValueModal } from '../components/shared/ToolUnlockValueModal';
import { FreeToolSurface } from '../data/free-tool-surface';
import { usePlansModalVisibility } from '../hooks/usePlansModalVisibility';
import { trackEvent } from '../lib/amplitude';
import { usePostUserToolsAccess } from '../services/hooks';
import { getErrorMessage } from '../utils/getErrorMessage';

export type { ToolUnlockModalEntry };

export type FreeToolsInactiveGateContextValue = {
  isInactiveLocked: boolean;
  isGeneratingToolsAccessLink: boolean;
  isUnlockModalOpen: boolean;
  unlockModalEntry: ToolUnlockModalEntry;
  unlockModalToolPath: string;
  openUnlockModal: (options?: { entry?: ToolUnlockModalEntry; toolPath?: string }) => void;
  closeUnlockModal: () => void;
  purchaseToolsAccess: () => void;
  openInactivePlanModal: (options?: { force?: boolean }) => void;
};

const defaultValue: FreeToolsInactiveGateContextValue = {
  isInactiveLocked: false,
  isGeneratingToolsAccessLink: false,
  isUnlockModalOpen: false,
  unlockModalEntry: 'download',
  unlockModalToolPath: '',
  openUnlockModal: () => {},
  closeUnlockModal: () => {},
  purchaseToolsAccess: () => {},
  openInactivePlanModal: () => {},
};

const FreeToolsInactiveGateContext = createContext<FreeToolsInactiveGateContextValue>(defaultValue);

export function FreeToolsInactiveGateProvider({
  isInactiveLocked,
  surface: _surface,
  children,
}: {
  isInactiveLocked: boolean;
  surface: FreeToolSurface | null;
  children: ReactNode;
}) {
  const router = useRouter();
  const { mutate: generateToolsAccessLink, isPending: isGeneratingToolsAccessLink } = usePostUserToolsAccess();

  const setPlansModalVisible = usePlansModalVisibility((s) => s.setIsVisible);
  const isModalVisible = usePlansModalVisibility((s) => s.isVisible);

  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);
  const [unlockModalEntry, setUnlockModalEntry] = useState<ToolUnlockModalEntry>('download');
  const [unlockModalToolPath, setUnlockModalToolPath] = useState('');

  const showModal = useCallback(() => {
    setPlansModalVisible(true, 'free_tool_gate');
  }, [setPlansModalVisible]);

  const openInactivePlanModal = useCallback(
    (_options?: { force?: boolean }) => {
      // Always open the plans modal on every blocked action — no toast fallback.
      if (isModalVisible) return;
      showModal();
    },
    [isModalVisible, showModal],
  );

  const closeUnlockModal = useCallback(() => {
    setIsUnlockModalOpen((wasOpen) => {
      if (wasOpen) {
        trackEvent('tool_unlock_modal_dismissed', {
          tool_path: unlockModalToolPath || router.pathname,
        });
      }
      return false;
    });
  }, [router.pathname, unlockModalToolPath]);

  const openUnlockModal = useCallback(
    (options?: { entry?: ToolUnlockModalEntry; toolPath?: string }) => {
      const toolPath = options?.toolPath || router.pathname;
      const entry = options?.entry ?? 'download';
      setUnlockModalToolPath(toolPath);
      setUnlockModalEntry(entry);
      setIsUnlockModalOpen(true);
      trackEvent('tool_unlock_modal_viewed', { tool_path: toolPath, entry });
    },
    [router.pathname],
  );

  const purchaseToolsAccess = useCallback(() => {
    const toolPath = unlockModalToolPath || router.pathname;
    trackEvent('tool_unlock_checkout_started', { tool_path: toolPath });

    generateToolsAccessLink(
      {},
      {
        onSuccess: ({ url }) => {
          const popup = window.open(url, '_blank');

          if (popup) {
            trackEvent('tool_unlock_checkout_redirect', { tool_path: toolPath });
            setIsUnlockModalOpen(false);
            return;
          }

          trackEvent('tool_unlock_checkout_failed', {
            tool_path: toolPath,
            error_message: 'popup_blocked',
          });
          addToast({
            title: 'Allow pop-ups for checkout, or continue in this tab.',
            color: 'warning',
            variant: 'flat',
          });
          setIsUnlockModalOpen(false);
          window.location.href = url;
        },
        onError: (error) => {
          const message = getErrorMessage(error);
          trackEvent('tool_unlock_checkout_failed', {
            tool_path: toolPath,
            error_message: message,
          });
          addToast({ title: message, color: 'danger', variant: 'flat' });
        },
      },
    );
  }, [generateToolsAccessLink, router.pathname, unlockModalToolPath]);

  const value = useMemo(
    () => ({
      isInactiveLocked,
      isGeneratingToolsAccessLink,
      isUnlockModalOpen,
      unlockModalEntry,
      unlockModalToolPath,
      openUnlockModal,
      closeUnlockModal,
      purchaseToolsAccess,
      openInactivePlanModal,
    }),
    [
      closeUnlockModal,
      isGeneratingToolsAccessLink,
      isInactiveLocked,
      isUnlockModalOpen,
      openInactivePlanModal,
      openUnlockModal,
      purchaseToolsAccess,
      unlockModalEntry,
      unlockModalToolPath,
    ],
  );

  return (
    <FreeToolsInactiveGateContext.Provider value={value}>
      {children}
      <ToolUnlockValueModal
        isOpen={isUnlockModalOpen}
        entry={unlockModalEntry}
        isLoading={isGeneratingToolsAccessLink}
        onClose={closeUnlockModal}
        onUnlock={purchaseToolsAccess}
        onStartTrial={() => {
          setIsUnlockModalOpen(false);
          openInactivePlanModal({ force: true });
        }}
      />
    </FreeToolsInactiveGateContext.Provider>
  );
}

export function useFreeToolsInactiveGate() {
  return useContext(FreeToolsInactiveGateContext);
}
